import { PackageSchema, SoftwareEnvironmentSpecSchema } from "@/api-client";

// Canonicalize package names following PEP 503 (same logic as Python packaging.utils.canonicalize_name)
export const canonicalizeName = (name: string): string => {
  return name.replace(/[-_.]+/g, "-").toLowerCase();
};

export type SoftwareSpecRow = PackageSchema & {
  requested: boolean;
  error: string | undefined;
};

export const unifyPackageSpec = (
  spec: SoftwareEnvironmentSpecSchema,
): SoftwareSpecRow[] => {
  const condaErrorPackages = spec.scanResult?.erroredPackages?.filter(
    (pkg) => pkg.source === "conda",
  );
  const pipErrorPackages = spec.scanResult?.erroredPackages?.filter(
    (pkg) => pkg.source === "pip",
  );
  const requestedCondaPackages = spec.scanResult?.packages?.filter(
    (pkg) => pkg.source === "conda" && pkg.requested,
  );
  const requestedPipPackages = spec.scanResult?.packages?.filter(
    (pkg) => pkg.source === "pip" && pkg.requested,
  );

  const packageMap = new Map<string, any>();

  // Add spec.packages first
  spec.packages.forEach((pkg) => {
    if (!pkg.name && !pkg.condaName) return;

    const name = pkg.condaName || pkg.name;
    const key = `${canonicalizeName(name)}-${pkg.source}`;
    packageMap.set(key, pkg);
  });

  // Merge scanResult.packages
  spec.scanResult?.packages?.forEach((scanPkg) => {
    const name = scanPkg.condaName || scanPkg.name;
    const key = `${canonicalizeName(name)}-${scanPkg.source}`;

    if (packageMap.has(key)) {
      const existingPkg = packageMap.get(key);
      packageMap.set(key, {
        ...existingPkg,
        clientVersion: existingPkg.clientVersion || scanPkg.version,
        channel: existingPkg.channel || scanPkg.channelUrl || scanPkg.channel,
      });
    } else {
      packageMap.set(key, {
        ...scanPkg,
        clientVersion: scanPkg.version,
        specifier: "",
        include: false,
      });
    }
  });

  // Merge errored packages
  spec.scanResult?.erroredPackages?.forEach((errorPkg) => {
    const name = errorPkg.condaName || errorPkg.name;
    const key = `${canonicalizeName(name)}-${errorPkg.source}`;

    if (packageMap.has(key)) {
      const existingPkg = packageMap.get(key);
      packageMap.set(key, {
        ...existingPkg,
        specifier: errorPkg.specifier || existingPkg.specifier || "",
      });
    } else {
      packageMap.set(key, {
        ...errorPkg,
        include: false,
        specifier: errorPkg.specifier || "",
      });
    }
  });

  const combinedRows = Array.from(packageMap.values()).map(
    (pkg, index): SoftwareSpecRow => ({
      id: pkg.id || index,
      include: false,
      specifier: "",
      ...pkg,
      requested:
        (pkg.source === "conda" &&
          requestedCondaPackages?.some(
            (requestedPkg) => requestedPkg.condaName === pkg.condaName,
          )) ||
        (pkg.source === "pip" &&
          requestedPipPackages?.some(
            (requestedPkg) => requestedPkg.name === pkg.name,
          )) ||
        false,
      error:
        (pkg.source === "conda"
          ? (() => {
              const condaErrorPkg = condaErrorPackages?.find(
                (errorPkg) => errorPkg.condaName === pkg.condaName,
              );
              return (
                condaErrorPkg?.error ||
                (condaErrorPkg ? "Package ignored" : undefined)
              );
            })()
          : pkg.source === "pip"
            ? (() => {
                const pipErrorPkg = pipErrorPackages?.find(
                  (errorPkg) => errorPkg.name === pkg.name,
                );
                return (
                  pipErrorPkg?.error ||
                  (pipErrorPkg ? "Package ignored" : undefined)
                );
              })()
            : undefined) ||
        ("include" in pkg && pkg.include === false
          ? "Package ignored"
          : undefined),
    }),
  );

  return combinedRows;
};
